import { Component, ElementRef, input, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BadgeModule } from '@tablesready/frontend-badge';
import { DateFormatPipe } from '@tablesready/frontend-pipes';
import { Notification, Inbound } from '@shared/party/models';

@Component({
  selector: 'lib-frontend-chat',
  standalone: true,
  imports: [CommonModule, BadgeModule, DateFormatPipe],
  templateUrl: './frontend-chat.component.html',
  styleUrl: './frontend-chat.component.scss',
})
export class FrontendChatComponent {
  messages = input.required<Array<Notification | Inbound>>();
  timezone = input.required<string>();

  private readonly timelineRef = viewChild<ElementRef<HTMLElement>>('timelineRef');

  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    const el = this.timelineRef()?.nativeElement;
    if (!el) {
      return;
    }
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }

  getMessageContent(message: Notification | Inbound): string {
    if (this.isInbound(message)) {
      return message.Body || '';
    } else if (this.isNotification(message)) {
      return message.message || '';
    }
    return '';
  }

  getMessageType(message: Notification | Inbound): string {
    if (this.isNotification(message)) {
      return message.type === 'sms'
        ? 'SMS'
        : message.type === 'voice'
        ? 'Call'
        : 'Message';
    }
    return 'Inbound SMS';
  }

  getMessageStatus(message: Notification | Inbound): string {
    if (this.isNotification(message)) {
      return message.status_sent || 'unknown';
    }
    return 'received';
  }

  getTypeBadgeClass(message: Notification | Inbound): string {
    if (this.isInbound(message)) {
      return 'badge badge-pill badge-success';
    }
    return 'badge badge-pill badge-info';
  }

  getStatusBadgeClass(message: Notification | Inbound): string {
    if (this.isInbound(message)) {
      return 'badge badge-pill badge-success';
    }
    if (this.isNotification(message)) {
      if (message.status_sent === 'sent' && message.success) {
        return 'badge badge-pill badge-success';
      }
      if (message.status_sent === 'inProcess') {
        return 'badge badge-pill badge-warning';
      }
    }
    return 'badge badge-pill badge-danger';
  }

  getStatusLabel(message: Notification | Inbound): string {
    if (this.isInbound(message)) {
      return 'Received';
    }
    if (this.isNotification(message)) {
      if (message.status_sent === 'sent' && message.success) {
        return 'Delivered';
      }
      if (message.status_sent === 'inProcess') {
        return 'Pending';
      }
    }
    return 'Failed';
  }

  getFailureReason(message: Notification | Inbound): string | null {
    if (this.isNotification(message) && message.status_sent === 'not_sent') {
      return message.data?.failureReason ?? null;
    }
    return null;
  }

  isNotification(message: Notification | Inbound): message is Notification {
    return 'status_sent' in message;
  }

  isInbound(message: Notification | Inbound): message is Inbound {
    return 'Body' in message;
  }

  trackByMessageId(index: number, item: Notification | Inbound): string {
    return item._id;
  }
}
